import DrawerMenuItemLink from 'Components/Layout/DrawerMenu/DrawerMenuItemLink'
import type { NavLinkItem } from 'Components/Layout/DrawerMenu/types'
import CheckIcon from '@mui/icons-material/Check'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, CircularProgress, MenuItem, MenuList, Typography } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import ListItemText from '@mui/material/ListItemText'
import { styled } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import { useMatch, useResolvedPath } from 'react-router-dom'

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'block',
  padding: 0,
  borderRadius: 8,
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected
  },
  '&.Mui-selected:hover': {
    backgroundColor: theme.palette.action.selected
  }
}))

const StyledMenuItemContent = styled(Box, {
  shouldForwardProp: prop => prop !== 'isSubMenuItem'
})<{ isSubMenuItem?: boolean }>(({ theme, isSubMenuItem }) => ({
  display: 'flex',
  alignItems: 'center',
  paddingTop: theme.spacing(0.4),
  paddingBottom: theme.spacing(0.4),
  paddingLeft: theme.spacing(isSubMenuItem ? 4 : 3),
  paddingRight: theme.spacing(2),
  marginTop: theme.spacing(isSubMenuItem ? 0 : 0.5),
  marginBottom: theme.spacing(isSubMenuItem ? 0 : 0.5)
}))

const StyledDropDownIconContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '4px',
  display: 'flex',
  color: theme.palette.tertiary.main,
  '.MuiSvgIcon-root': {
    fontSize: '1.2rem'
  }
}))

interface DrawerMenuItemProps {
  navLinkItem: NavLinkItem
  rootUrl: string
  isSubMenuItem?: boolean
}

function DrawerMenuItem({
  navLinkItem,
  rootUrl,
  isSubMenuItem = false
}: Readonly<DrawerMenuItemProps>) {
  const { title, link, isSubMenuLoading = false, subMenuItems, suffix } = navLinkItem
  const [open, setOpen] = useState(!isSubMenuLoading)

  // Open the collapse when loading finishes
  useEffect(() => {
    if (subMenuItems) {
      if (isSubMenuLoading) {
        setOpen(false)
      } else {
        setOpen(true)
      }
    }
  }, [isSubMenuLoading, subMenuItems])

  // Use the same logic as React Router NavLink for determining active state
  const resolved = useResolvedPath(link)
  const match = useMatch({
    path: resolved.pathname,
    end: link === rootUrl
  })
  const isSelected = !!match

  const handleClick: React.MouseEventHandler<HTMLDivElement> = event => {
    if (subMenuItems) {
      event?.preventDefault()
      setOpen(!open)
    }
  }
  const hasSubMenu = !!subMenuItems
  const showCheckIcon = !hasSubMenu && isSelected && !isSubMenuItem

  return (
    <StyledMenuItem selected={isSelected && !hasSubMenu}>
      <DrawerMenuItemLink
        className={isSubMenuItem ? 'sub-menu-item' : 'menu-item'}
        to={isSubMenuItem ? { hash: '', pathname: link } : { pathname: link }}
        end={link === rootUrl}
        hasSubMenu={hasSubMenu}
      >
        <StyledMenuItemContent
          onClick={handleClick}
          isSubMenuItem={isSubMenuItem}
          sx={{ ...(hasSubMenu && { marginBottom: 0 }) }}
        >
          {hasSubMenu && (
            <StyledDropDownIconContainer>
              {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </StyledDropDownIconContainer>
          )}
          <ListItemText
            primary={title}
            title={title}
            sx={{ p: 1 }}
            slotProps={{
              primary: {
                fontSize: '0.875rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }
            }}
          />
          {showCheckIcon && <CheckIcon sx={{ fontSize: '1rem', color: 'tertiary.main' }} />}
          {isSubMenuLoading && <CircularProgress size={12} />}
          {suffix && (
            <Typography variant="body2" color="text.secondary">
              {suffix}
            </Typography>
          )}
        </StyledMenuItemContent>
        {subMenuItems && (
          <Collapse in={open} timeout={open ? 'auto' : 0} unmountOnExit>
            <MenuList disablePadding dense>
              {!isSubMenuLoading &&
                subMenuItems.map(subMenuItem => (
                  <DrawerMenuItem
                    key={`${title}-submenu-${subMenuItem.link}`}
                    navLinkItem={subMenuItem}
                    rootUrl={rootUrl}
                    isSubMenuItem
                  />
                ))}
            </MenuList>
          </Collapse>
        )}
      </DrawerMenuItemLink>
    </StyledMenuItem>
  )
}

export { DrawerMenuItem }
